import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown,
  ChevronRight,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  X,
} from "lucide-react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  buildWhatsAppUrl,
  companyInfo,
  footerStatement,
  mainNavigationLinks,
  navigationLinks,
  serviceDropdownLinks,
} from "../../content/siteContent";

const linkClass = ({ isActive }) =>
  `transition-colors duration-300 ${
    isActive ? "text-safety" : "text-white/80 hover:text-safety"
  }`;

const Layout = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const { pathname } = useLocation();

  const isServiceRoute = useMemo(
    () => ["/servicos", ...serviceDropdownLinks.map((item) => item.path)].includes(pathname),
    [pathname]
  );

  useEffect(() => {
    setMenuOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [menuOpen]);

  return (
    <div className="site-shell" data-testid="site-shell">
      <header
        className="fixed inset-x-0 top-0 z-50 border-b border-white/10 glass-shell"
        data-testid="site-header"
      >
        <div className="container-shell flex items-center justify-between gap-4 py-2 md:py-3">
          <Link
            to="/"
            className="flex items-center gap-3 rounded-sm bg-white/95 px-3 py-2 shadow-[0_18px_40px_rgba(0,0,0,0.18)]"
            data-testid="site-logo-link"
            aria-label="Voltar para a página inicial"
          >
            <img
              src={companyInfo.logoUrl}
              alt={companyInfo.name}
              className="h-20 w-auto md:h-24"
              loading="eager"
              fetchPriority="high"
              data-testid="site-logo-image"
            />
          </Link>

          <nav
            className="hidden xl:flex xl:items-center xl:gap-7 xl:text-[11px] xl:font-semibold xl:uppercase xl:tracking-[0.18em]"
            data-testid="desktop-navigation"
          >
            {mainNavigationLinks.map((item) => {
              if (!item.hasDropdown) {
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={linkClass}
                    data-testid={`nav-link-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {item.name}
                  </NavLink>
                );
              }

              return (
                <div
                  key={item.path}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <button
                    type="button"
                    className={`inline-flex items-center gap-2 transition-colors duration-300 ${
                      isServiceRoute ? "text-safety" : "text-white/80 hover:text-safety"
                    }`}
                    onClick={() => setServicesOpen((current) => !current)}
                    data-testid="nav-services-dropdown-button"
                    aria-expanded={servicesOpen}
                  >
                    Serviços
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-300 ${
                        servicesOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <AnimatePresence>
                    {servicesOpen ? (
                      <motion.div
                        initial={{ opacity: 0, y: 18, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 14, scale: 0.98 }}
                        transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                        className="service-dropdown-panel absolute left-1/2 top-full mt-5 w-[340px] -translate-x-1/2 overflow-hidden"
                        data-testid="services-dropdown-panel"
                      >
                        <div className="service-dropdown-grid p-3">
                          <Link
                            to="/servicos"
                            className="service-dropdown-item group"
                            data-testid="services-dropdown-overview-link"
                          >
                            <div>
                              <div className="text-xs uppercase tracking-[0.28em] text-safety">
                                Visão geral
                              </div>
                              <div className="mt-2 font-heading text-2xl uppercase text-white">
                                Todos os serviços
                              </div>
                            </div>
                            <ChevronRight className="h-4 w-4 text-safety transition-transform duration-300 group-hover:translate-x-1" />
                          </Link>

                          {serviceDropdownLinks.map((service) => (
                            <Link
                              key={service.path}
                              to={service.path}
                              className="service-dropdown-item group"
                              data-testid={`services-dropdown-link-${service.id}`}
                            >
                              <span className="font-heading text-xl uppercase text-white/90">
                                {service.name}
                              </span>
                              <ChevronRight className="h-4 w-4 text-safety transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
              );
            })}
          </nav>

          <div className="hidden items-center gap-3 xl:flex">
            <a
              href={buildWhatsAppUrl("Olá, gostaria de solicitar um orçamento para serviços elétricos.")}
              target="_blank"
              rel="noreferrer"
              className="primary-button px-5 py-3 text-sm"
              data-testid="header-cta-button"
            >
              Solicitar orçamento
            </a>
          </div>

          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center border border-white/15 text-white xl:hidden"
            onClick={() => setMenuOpen((current) => !current)}
            data-testid="mobile-menu-toggle-button"
            aria-label="Abrir menu"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      {menuOpen ? (
        <div
          className="fixed inset-0 z-40 bg-brand/95 px-6 pb-8 pt-24 xl:hidden"
          data-testid="mobile-navigation-panel"
        >
          <div className="mx-auto flex max-w-sm flex-col gap-4">
            {mainNavigationLinks.map((item) => {
              if (!item.hasDropdown) {
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className="flex items-center justify-between border-b border-white/10 py-3 font-heading text-2xl uppercase tracking-[0.12em] text-white"
                    data-testid={`mobile-nav-link-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <span>{item.name}</span>
                    <ChevronRight className="h-4 w-4 text-safety" />
                  </NavLink>
                );
              }

              return (
                <div key={item.path} className="border-b border-white/10 py-3" data-testid="mobile-services-group">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between font-heading text-2xl uppercase tracking-[0.12em] text-white"
                    onClick={() => setMobileServicesOpen((current) => !current)}
                    data-testid="mobile-services-dropdown-button"
                    aria-expanded={mobileServicesOpen}
                  >
                    <span>Serviços</span>
                    <ChevronDown
                      className={`h-4 w-4 text-safety transition-transform duration-300 ${
                        mobileServicesOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <AnimatePresence>
                    {mobileServicesOpen ? (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                        data-testid="mobile-services-dropdown-panel"
                      >
                        <div className="mt-4 grid gap-3 pl-4">
                          <NavLink
                            to="/servicos"
                            className="text-sm uppercase tracking-[0.22em] text-white/70"
                            data-testid="mobile-services-overview-link"
                          >
                            Todos os serviços
                          </NavLink>
                          {serviceDropdownLinks.map((service) => (
                            <NavLink
                              key={service.path}
                              to={service.path}
                              className="text-lg uppercase tracking-[0.16em] text-white/90"
                              data-testid={`mobile-services-link-${service.id}`}
                            >
                              {service.name}
                            </NavLink>
                          ))}
                        </div>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
              );
            })}

            <a
              href={buildWhatsAppUrl("Olá, gostaria de solicitar um orçamento para serviços elétricos.")}
              target="_blank"
              rel="noreferrer"
              className="primary-button mt-4 w-full"
              data-testid="mobile-menu-cta-button"
            >
              Solicitar orçamento
            </a>
          </div>
        </div>
      ) : null}

      <main className="page-top-spacing" data-testid="site-main-content">
        <Outlet />
      </main>

      <footer className="mt-20 bg-brand text-white" data-testid="site-footer">
        <div className="container-shell grid gap-12 py-16 lg:grid-cols-[1.3fr_0.8fr_0.8fr]">
          <div className="space-y-5">
            <div className="flex items-center gap-4" data-testid="footer-brand-block">
              <img
                src={companyInfo.logoUrl}
                alt={`${companyInfo.name} logotipo`}
                className="h-16 w-auto"
                loading="lazy"
                data-testid="footer-logo-image"
              />
              <div className="min-w-0">
                <div
                  className="font-heading text-3xl uppercase tracking-[0.16em]"
                  data-testid="footer-brand-title"
                >
                  W-Electric
                </div>
                <div
                  className="text-xs uppercase tracking-[0.32em] text-white/60"
                  data-testid="footer-brand-subtitle"
                >
                  Engenharia Elétrica
                </div>
              </div>
            </div>
            <p className="max-w-xl body-copy text-white/75" data-testid="footer-statement-text">
              {footerStatement}
            </p>
            <div className="grid gap-3 text-sm text-white/85">
              <a
                href={companyInfo.phoneHref}
                className="inline-flex items-center gap-3 transition-colors hover:text-safety"
                data-testid="footer-phone-link"
              >
                <Phone className="h-4 w-4 text-safety" />
                <span data-testid="footer-phone-text">{companyInfo.phoneDisplay}</span>
              </a>
              <a
                href={companyInfo.emailHref}
                className="inline-flex items-center gap-3 transition-colors hover:text-safety"
                data-testid="footer-email-link"
              >
                <Mail className="h-4 w-4 text-safety" />
                <span data-testid="footer-email-text">{companyInfo.email}</span>
              </a>
              <div className="inline-flex items-center gap-3" data-testid="footer-city-row">
                <MapPin className="h-4 w-4 text-safety" />
                <span data-testid="footer-city-text">Atendimento em {companyInfo.city}</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-heading text-2xl uppercase tracking-[0.16em]" data-testid="footer-nav-title">
              Páginas
            </h3>
            <div className="mt-5 grid gap-3 text-sm uppercase tracking-[0.12em] text-white/75">
              {navigationLinks.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="transition-colors hover:text-safety"
                  data-testid={`footer-link-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="space-y-5">
            <h3 className="font-heading text-2xl uppercase tracking-[0.16em]" data-testid="footer-cta-title">
              Fale com a engenharia
            </h3>
            <p className="body-copy text-white/75" data-testid="footer-cta-description">
              Solicite uma análise técnica e receba atendimento focado em segurança, confiabilidade e conformidade.
            </p>
            <a
              href={buildWhatsAppUrl("Olá, preciso de suporte para manutenção elétrica em São Paulo.")}
              target="_blank"
              rel="noreferrer"
              className="primary-button w-full justify-center"
              data-testid="footer-cta-button"
            >
              Solicitar orçamento
            </a>
          </div>
        </div>
      </footer>

      <a
        href={buildWhatsAppUrl("Olá, gostaria de solicitar um orçamento para serviços elétricos.")}
        target="_blank"
        rel="noreferrer"
        className="signal-pulse fixed bottom-5 right-5 z-50 inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_18px_40px_rgba(37,211,102,0.35)]"
        aria-label="Abrir conversa no WhatsApp"
        data-testid="floating-whatsapp-button"
      >
        <MessageCircle className="h-8 w-8" />
      </a>
    </div>
  );
};

export default Layout;